import { FC, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CancelIcon from '@mui/icons-material/Cancel';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import SendIcon from '@mui/icons-material/Send';
import User from "../../../../Models/UserModels/User";
import UserHarbours from "../../../../Models/UserModels/UserHarbours";
import Notification from "../../../../Models/NotificationModels/Notification";
import NotificationRecipient from "../../../../Models/NotificationModels/NotificationRecipient";
import NotificationSend from "../../../../Models/NotificationModels/NotificationSend";
import AdminNotificationSummary from "../../../../Models/NotificationModels/AdminNotificationSummary";
import AdminNotificationDetailResponse from "../../../../Models/NotificationModels/AdminNotificationDetailResponse";
import {
  getAdminNotificationDetail,
  getAdminNotifications,
  getNotificationRecipients,
  sendNotification,
} from "../../../../Services/LondunarkerfiAPIService";
import { MobilePaperComponent, PaperComponent } from "../../../Paper/CustomPaper";
import { logError } from "../../../../Helpers/LogError";

interface NotificationModalProps {
  open: boolean;
  toggleOpen: () => void;
  showSnackbar?: (message: string, severity: string) => void;
  user: User;
  harbours?: UserHarbours[];
  isAdmin?: boolean;
  notifications: Notification[];
  notificationsLoading: boolean;
  onMarkAllRead: () => void;
  onNotificationClick: (notification: Notification) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  tabValue: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, tabValue, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={tabValue !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
    >
      {tabValue === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `notification-tab-${index}`,
    'aria-controls': `notification-tabpanel-${index}`,
  };
}

const NotificationModal: FC<NotificationModalProps> = (props: NotificationModalProps) => {
  const [tabValue, setTabValue] = useState(0);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [info, setInfo] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState<NotificationRecipient | null>(null);
  const [selectedHarbour, setSelectedHarbour] = useState<UserHarbours | null>(null);
  const [recipients, setRecipients] = useState<NotificationRecipient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendTabValue, setSendTabValue] = useState(0);
  const [adminNotifications, setAdminNotifications] = useState<AdminNotificationSummary[]>([]);
  const [adminNotificationsLoading, setAdminNotificationsLoading] = useState(false);
  const [adminDetailOpen, setAdminDetailOpen] = useState(false);
  const [adminDetailLoading, setAdminDetailLoading] = useState(false);
  const [adminDetail, setAdminDetail] = useState<AdminNotificationDetailResponse | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const unreadCount = props.notifications.filter((notification) => !notification.isRead).length;

  useEffect(() => {
    if (!props.open) {
      setTabValue(0);
      setTitle('');
      setMessage('');
      setInfo('');
      setSelectedRecipient(null);
      setSelectedHarbour(null);
      setRecipients([]);
      setSearchTerm('');
      setSending(false);
      setSendTabValue(0);
      setAdminDetailOpen(false);
      setAdminDetail(null);
    }
  }, [props.open]);

  useEffect(() => {
    if (!props.open || tabValue !== 1 || sendTabValue !== 0 || !props.isAdmin) {
      return;
    }

    if (searchTerm.trim().length < 2) {
      setRecipients([]);
      setRecipientsLoading(false);
      return;
    }

    setRecipientsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await getNotificationRecipients(searchTerm.trim());
        setRecipients(result);
      } catch (error) {
        logError(error);
      } finally {
        setRecipientsLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm, tabValue, sendTabValue, props.open, props.isAdmin]);

  useEffect(() => {
    if (!props.open || !props.isAdmin || tabValue !== 2) {
      return;
    }

    let isActive = true;
    setAdminNotificationsLoading(true);
    getAdminNotifications()
      .then((items) => {
        if (!isActive) {
          return;
        }
        setAdminNotifications(items || []);
      })
      .finally(() => {
        if (isActive) {
          setAdminNotificationsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [props.open, props.isAdmin, tabValue]);

  const handleClose = () => {
    props.toggleOpen();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedRecipient(null);
    setSelectedHarbour(null);
  };

  const handleSendTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSendTabValue(newValue);
    setSelectedRecipient(null);
    setSelectedHarbour(null);
  };

  const handleNotificationOpen = (notification: Notification) => {
    props.onNotificationClick(notification);
  };

  const handleAdminNotificationOpen = async (notificationId: number) => {
    setAdminDetailOpen(true);
    setAdminDetailLoading(true);
    try {
      const detail = await getAdminNotificationDetail(notificationId);
      if (!detail) {
        props.showSnackbar?.('Gat ekki náð í tilkynningu.', 'error');
        return;
      }
      setAdminDetail(detail);
    } catch (error) {
      logError(error);
      props.showSnackbar?.('Gat ekki náð í tilkynningu.', 'error');
    } finally {
      setAdminDetailLoading(false);
    }
  };

  const refreshAdminNotifications = async () => {
    setAdminNotificationsLoading(true);
    try {
      const items = await getAdminNotifications();
      setAdminNotifications(items || []);
    } finally {
      setAdminNotificationsLoading(false);
    }
  };

  const handleAdminDetailClose = () => {
    setAdminDetailOpen(false);
  };

  const formatNotificationDate = (value?: string) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const sendDisabled = useMemo(() => {
    if (!title.trim() || !message.trim()) {
      return true;
    }
    if (tabValue === 1 && sendTabValue === 0 && !selectedRecipient) {
      return true;
    }
    if (tabValue === 1 && sendTabValue === 1 && !selectedHarbour) {
      return true;
    }
    return false;
  }, [title, message, tabValue, sendTabValue, selectedRecipient, selectedHarbour]);

  const handleSend = async () => {
    if (sendDisabled || sending) {
      return;
    }

    const payload: NotificationSend = {
      title: title.trim(),
      message: message.trim(),
      info: info.trim() ? info.trim() : undefined,
      targetType: sendTabValue === 0 ? "User" : sendTabValue === 1 ? "Harbour" : "All",
      userId: sendTabValue === 0 ? selectedRecipient?.id : undefined,
      harbourId: sendTabValue === 1 ? selectedHarbour?.id : undefined,
    };

    try {
      setSending(true);
      await sendNotification(payload);
      props.showSnackbar?.('Tilkynning send.', 'success');
      setTitle('');
      setMessage('');
      setInfo('');
      setSelectedRecipient(null);
      setSelectedHarbour(null);
      setSearchTerm('');
      props.toggleOpen();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logError(error);
      props.showSnackbar?.('Villa kom upp við að senda tilkynningu.', 'error');
    } finally {
      setSending(false);
    }
  };

  const getAdminStats = (item: AdminNotificationSummary) => {
    const unread = Math.max(item.recipientsCount - item.readCount, 0);
    return { unread, read: item.readCount, total: item.recipientsCount };
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={handleClose}
        PaperComponent={isMobile || isTablet ? MobilePaperComponent : PaperComponent}
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', minHeight: { xs: '70vh', md: 620 } } }}
        aria-labelledby="notification-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          id="notification-dialog-title"
          sx={{
            fontSize: '1.35em',
            fontWeight: 700,
            display: 'flex',
            justifyContent: 'space-between',
            gap: '0.4em',
            lineHeight: '1.5em',
            cursor: 'move',
            bgcolor: 'background.default',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          Tilkynningar
          <IconButton sx={{ position: 'absolute', top: 0, right: 0 }} onClick={handleClose}>
            <CancelIcon fontSize='large' />
          </IconButton>
        </DialogTitle>

        <Box
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            minHeight: { xs: 520, md: 560 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {props.isAdmin ? (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="notification-tabs"
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 42 },
                '& .MuiTabs-indicator': { height: 3, borderRadius: 2 },
              }}
            >
              <Tab
                label={(
                  <Stack direction="row" spacing={1} alignItems="center">
                    <NotificationsNoneIcon fontSize="small" />
                    <span>Mínar tilkynningar</span>
                  </Stack>
                )}
                {...a11yProps(0)}
              />
              <Tab
                label={(
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CampaignIcon fontSize="small" />
                    <span>Senda tilkynningu</span>
                  </Stack>
                )}
                {...a11yProps(1)}
              />
              <Tab
                label={(
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MailOutlineIcon fontSize="small" />
                    <span>Saga tilkynninga</span>
                  </Stack>
                )}
                {...a11yProps(2)}
              />
            </Tabs>
          ) : (
            <Box sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <NotificationsNoneIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Mínar tilkynningar</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Yfirlit yfir tilkynningar sem varða aðganginn þinn.
              </Typography>
            </Box>
          )}

          {props.isAdmin && <Divider />}

          <CustomTabPanel tabValue={props.isAdmin ? tabValue : 0} index={0}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                {props.isAdmin && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <NotificationsNoneIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>Mínar tilkynningar</Typography>
                  </Stack>
                )}
                {props.notifications.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Smelltu á tilkynningu til að opna.
                  </Typography>
                )}
              </Box>
              <Button variant="contained" size="small" onClick={props.onMarkAllRead} disabled={props.notifications.length === 0 || unreadCount === 0}>
                Merkja allt lesið
              </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
              {props.notificationsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : props.notifications.length === 0 ? (
                <Box sx={{ px: 3, py: 4 }}>
                  <Typography color="text.secondary">Engar tilkynningar í augnablikinu.</Typography>
                </Box>
              ) : (
                <List sx={{ p: 0, maxHeight: { xs: 320, md: 360 }, overflowY: 'auto' }}>
                  {props.notifications.map((notification, index) => (
                    <Box key={notification.id}>
                      <ListItemButton alignItems="flex-start" onClick={() => handleNotificationOpen(notification)} sx={{ gap: 2, py: 1.5 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: notification.isRead ? 'transparent' : theme.palette.primary.main,
                            mt: 1,
                            border: notification.isRead ? `1px solid ${theme.palette.divider}` : 'none',
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                            <Typography fontWeight={notification.isRead ? 600 : 700}>{notification.title}</Typography>
                            {notification.createdAt && (
                              <Typography variant="caption" color="text.secondary">
                                {formatNotificationDate(notification.createdAt)}
                              </Typography>
                            )}
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                            {notification.message}
                          </Typography>
                          {notification.info && (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line', display: 'block', mt: 0.75 }}>
                              {notification.info}
                            </Typography>
                          )}
                        </Box>
                        <MailOutlineIcon fontSize="small" color={notification.isRead ? 'disabled' : 'primary'} />
                      </ListItemButton>
                      {index < props.notifications.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              )}
            </Paper>
          </CustomTabPanel>

          {props.isAdmin && (
            <CustomTabPanel tabValue={tabValue} index={1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: { xs: 420, md: 460 } }}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <PersonOutlineIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={700}>Móttakandi</Typography>
                    </Stack>
                    <Tabs
                      value={sendTabValue}
                      onChange={handleSendTabChange}
                      aria-label="notification-target-tabs"
                      sx={{
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40 },
                        '& .MuiTabs-indicator': { height: 3, borderRadius: 2 },
                      }}
                    >
                      <Tab
                        label={(
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PersonOutlineIcon fontSize="small" />
                            <span>Einn notandi</span>
                          </Stack>
                        )}
                      />
                      <Tab
                        label={(
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LocationOnOutlinedIcon fontSize="small" />
                            <span>Höfn</span>
                          </Stack>
                        )}
                      />
                      <Tab
                        label={(
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PublicOutlinedIcon fontSize="small" />
                            <span>Allir</span>
                          </Stack>
                        )}
                      />
                    </Tabs>
                    {sendTabValue === 0 && (
                      <Box sx={{ pt: 2 }}>
                        <Autocomplete
                          value={selectedRecipient}
                          onChange={(event, newValue) => setSelectedRecipient(newValue)}
                          inputValue={searchTerm}
                          onInputChange={(event, newInputValue) => setSearchTerm(newInputValue)}
                          options={recipients}
                          getOptionLabel={(option) => `${option.name}${option.email ? ` (${option.email})` : ''}`}
                          loading={recipientsLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Leita að notanda"
                              placeholder="Skrifa a.m.k. 2 stafi..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {recipientsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>
                    )}

                    {sendTabValue === 1 && (
                      <Box sx={{ pt: 2 }}>
                        <Autocomplete
                          value={selectedHarbour}
                          onChange={(event, newValue) => setSelectedHarbour(newValue)}
                          options={props.harbours || []}
                          getOptionLabel={(option) => `${option.harbourName} (${option.harbourNumber})`}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Veldu höfn"
                            />
                          )}
                        />
                      </Box>
                    )}

                    {sendTabValue === 2 && (
                      <Box sx={{ pt: 2 }}>
                        <Typography sx={{ color: 'text.secondary' }}>
                          Tilkynningin fer til allra notenda í kerfinu.
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <MailOutlineIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={700}>Skilaboð</Typography>
                    </Stack>
                    <Stack spacing={2}>
                      <TextField
                        label="Titill"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        fullWidth
                        inputProps={{ maxLength: 120 }}
                        helperText={`${title.length}/120`}
                      />
                      <TextField
                        label="Skilaboð"
                        value={message}
                        onChange={(event) => setMessage(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.stopPropagation();
                          }
                        }}
                        fullWidth
                        multiline
                        minRows={4}
                      />
                      <TextField
                        label="Upplýsingar (valfrjálst)"
                        value={info}
                        onChange={(event) => setInfo(event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Stack>
                  </Paper>
                </Box>

                <Divider />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                  <Button variant="outlined" onClick={handleClose} disabled={sending}>
                    Hætta við
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleSend}
                    disabled={sendDisabled || sending}
                  >
                    Senda tilkynningu
                  </Button>
                </Stack>
              </Box>
            </CustomTabPanel>
          )}

          {props.isAdmin && (
            <CustomTabPanel tabValue={tabValue} index={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CampaignIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>Saga tilkynninga</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Yfirlit yfir allar tilkynningar sem hafa verið sendar.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={refreshAdminNotifications}
                  disabled={adminNotificationsLoading}
                >
                  Uppfæra
                </Button>
              </Stack>

              <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                {adminNotificationsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : adminNotifications.length === 0 ? (
                  <Box sx={{ px: 3, py: 4 }}>
                    <Typography color="text.secondary">Engar tilkynningar í sögunni.</Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0, maxHeight: { xs: 320, md: 360 }, overflowY: 'auto' }}>
                    {adminNotifications.map((item, index) => {
                      const stats = getAdminStats(item);
                      return (
                        <Box key={item.id}>
                          <ListItemButton alignItems="flex-start" onClick={() => handleAdminNotificationOpen(item.id)} sx={{ gap: 2, py: 1.5 }}>
                            <CampaignIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Typography fontWeight={700}>{item.title}</Typography>
                                {item.createdAt && (
                                  <Typography variant="caption" color="text.secondary">
                                    {formatNotificationDate(item.createdAt)}
                                  </Typography>
                                )}
                              </Stack>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                                {item.message}
                              </Typography>
                              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <GroupsOutlinedIcon fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    {stats.total} móttakendur
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <MarkEmailReadOutlinedIcon fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    {stats.read} lesið
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <MarkEmailUnreadOutlinedIcon fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    {stats.unread} ólesið
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Box>
                            <MailOutlineIcon fontSize="small" color="action" />
                          </ListItemButton>
                          {index < adminNotifications.length - 1 && <Divider />}
                        </Box>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </CustomTabPanel>
          )}
        </Box>
      </Dialog>
      <Dialog
        open={adminDetailOpen}
        onClose={handleAdminDetailClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: `1px solid ${theme.palette.divider}` }}>
          Tilkynning - Yfirlit
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {adminDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !adminDetail ? (
            <Typography color="text.secondary">Engin gögn fundust.</Typography>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>{adminDetail.notification.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {adminDetail.notification.senderName ? `Frá: ${adminDetail.notification.senderName}` : 'Frá: Ótilgreint'}
                </Typography>
                {adminDetail.notification.createdAt && (
                  <Typography variant="caption" color="text.secondary">
                    {formatNotificationDate(adminDetail.notification.createdAt)}
                  </Typography>
                )}
              </Box>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography sx={{ whiteSpace: 'pre-line' }}>{adminDetail.notification.message}</Typography>
                {adminDetail.notification.info && (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 1.5 }}>
                    {adminDetail.notification.info}
                  </Typography>
                )}
              </Paper>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GroupsOutlinedIcon color="primary" />
                    <Typography fontWeight={700}>Móttakendur</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {adminDetail.notification.recipientsCount} alls, {adminDetail.notification.readCount} lesið
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MarkEmailUnreadOutlinedIcon color="primary" />
                    <Typography fontWeight={700}>Ólesið</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {Math.max(adminDetail.notification.recipientsCount - adminDetail.notification.readCount, 0)} ólesið
                  </Typography>
                </Paper>
              </Stack>

              <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default', borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography fontWeight={700}>Móttakendur</Typography>
                </Box>
                <List sx={{ p: 0, maxHeight: 320, overflowY: 'auto' }}>
                  {adminDetail.recipients.map((recipient, index) => (
                    <Box key={recipient.id}>
                      <ListItemButton disableRipple sx={{ gap: 2, alignItems: 'flex-start' }}>
                        {recipient.isRead ? (
                          <MarkEmailReadOutlinedIcon color="success" />
                        ) : (
                          <MarkEmailUnreadOutlinedIcon color="action" />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={600}>{recipient.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {recipient.email || 'Ekki skráð netfang'}
                          </Typography>
                          {recipient.harbourName && (
                            <Typography variant="caption" color="text.secondary">
                              Höfn: {recipient.harbourName}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {recipient.isRead && recipient.readAt ? `Lesið: ${formatNotificationDate(recipient.readAt)}` : 'Ólesið'}
                        </Typography>
                      </ListItemButton>
                      {index < adminDetail.recipients.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleAdminDetailClose}>Loka</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationModal;
